import React, { useEffect, useState } from 'react';
import { PhoneIcon, UserIcon, TagIcon, TrendingUpIcon, AlertTriangleIcon, SearchIcon, CheckCircleIcon, ChevronRightIcon, ChevronLeftIcon, SaveIcon, PackageIcon, PlusIcon, XIcon, MapPinIcon, ClockIcon, DollarSignIcon, TrashIcon, HistoryIcon, CalendarIcon, EyeIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Badge } from '../components/ui/Badge';
import { Modal, ModalBody } from '../components/ui/Modal';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { supabaseAdmin } from '../lib/supabase';
import { Client, Product } from '../types';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7;
type TabType = 'speech' | 'historial';
type FlowType = 'ofertas' | 'alta_rotacion' | 'vencimientos' | 'validar_riesgo' | '';
type CallResult = 'venta_realizada' | 'promesa_compra' | 'promesa_cobro' | 'no_interesado' | 'seguimiento' | 'no_contesto' | 'ocupado' | '';
interface SelectedProduct {
  productoId: string;
  nombre: string;
  sku: string;
  precio: number;
}
interface VencidoCliente {
  productoId: string;
  nombre: string;
  sku: string;
  cantidad: number;
  notas: string;
}
export function CallCenterPage() {
  const {
    user
  } = useAuth();
  const {
    clients
  } = useApp();
  const [activeTab, setActiveTab] = useState<TabType>('speech');
  const [callsHistory, setCallsHistory] = useState<any[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedCall, setSelectedCall] = useState<any | null>(null);
  // Wizard State
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  // Call Data State
  const [selectedClientId, setSelectedClientId] = useState('');
  const [flow, setFlow] = useState<FlowType>('');
  const [vencidosCliente, setVencidosCliente] = useState<VencidoCliente[]>([]);
  const [offeredProducts, setOfferedProducts] = useState<SelectedProduct[]>([]);
  const [interestedProducts, setInterestedProducts] = useState<SelectedProduct[]>([]);
  const [callResult, setCallResult] = useState<CallResult>('');
  const [notes, setNotes] = useState('');
  // Inventory State
  const [inventory, setInventory] = useState<Product[]>([]);
  const [promotions, setPromotions] = useState<string[]>([]);
  const [topProducts, setTopProducts] = useState<Record<string, number>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingInventory, setIsLoadingInventory] = useState(false);
  // Vencidos Form State
  const [vencidoSearch, setVencidoSearch] = useState('');
  const [vencidoQty, setVencidoQty] = useState(1);
  const [vencidoNotes, setVencidoNotes] = useState('');
  const selectedClient = clients.find(c => c.id === selectedClientId);
  const fetchHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('call_center_calls').select('*').order('fecha', {
        ascending: false
      }).limit(100);
      if (error) throw error;
      if (data) setCallsHistory(data);
    } catch (err) {
      console.error('Error fetching call history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  };
  useEffect(() => {
    if (activeTab === 'historial') {
      fetchHistory();
    }
  }, [activeTab]);
  useEffect(() => {
    const loadData = async () => {
      setIsLoadingInventory(true);
      try {
        // EMERGENCY: Single query with limit instead of while(hasMore) loop
        const {
          data,
          error
        } = await supabaseAdmin.from('products').select('*').order('nombre').limit(2000);
        if (!error && data) {
          const allProducts: Product[] = data.map((p: any) => ({
            id: p.id,
            nombre: p.nombre,
            sku: p.sku,
            stock: p.stock,
            stockMinimo: p.stock_minimo ?? 0,
            precio: p.precio,
            subTotal: p.sub_total || 0,
            itbis: p.itbis || 0,
            proveedor: p.proveedor,
            fechaVencimiento: p.fecha_vencimiento || null,
            estado: p.estado
          }));
          setInventory(allProducts);
          // Load active promotions
          const {
            data: promoData
          } = await supabaseAdmin.from('product_promotions').select('product_id').eq('activo', true);
          if (promoData) {
            setPromotions(promoData.map(p => p.product_id));
          }
          // Load top products (alta rotacion)
          const {
            data: orderLines
          } = await supabaseAdmin.from('order_lines').select('producto_id, cantidad');
          if (orderLines) {
            const counts: Record<string, number> = {};
            orderLines.forEach(line => {
              if (line.producto_id) {
                counts[line.producto_id] = (counts[line.producto_id] || 0) + line.cantidad;
              }
            });
            setTopProducts(counts);
          }
        }
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setIsLoadingInventory(false);
      }
    };
    loadData();
  }, []);
  const handleNext = () => {
    if (currentStep === 2 && flow !== 'validar_riesgo') {
      setCurrentStep(4); // Skip step 3 if not validating risk
    } else if (currentStep < 7) {
      setCurrentStep(prev => prev + 1 as Step);
    }
  };
  const handleBack = () => {
    if (currentStep === 4 && flow !== 'validar_riesgo') {
      setCurrentStep(2); // Go back to 2 if we skipped 3
    } else if (currentStep > 1) {
      setCurrentStep(prev => prev - 1 as Step);
    }
  };
  const resetForm = () => {
    setCurrentStep(1);
    setSelectedClientId('');
    setFlow('');
    setVencidosCliente([]);
    setOfferedProducts([]);
    setInterestedProducts([]);
    setCallResult('');
    setNotes('');
    setSubmitSuccess(false);
  };
  const handleSaveCall = async () => {
    if (!selectedClient || !user || !flow || !callResult) return;
    setIsSubmitting(true);
    try {
      const {
        error
      } = await supabaseAdmin.from('call_center_calls').insert({
        agente_id: user.id,
        agente_nombre: user.nombre,
        cliente_id: selectedClient.id,
        cliente_nombre: selectedClient.nombre,
        cliente_telefono: selectedClient.telefono,
        cliente_zona: selectedClient.zona,
        flujo_seleccionado: flow,
        productos_ofrecidos: offeredProducts,
        productos_interes: interestedProducts,
        productos_vencidos_cliente: vencidosCliente,
        tiene_productos_vencidos: vencidosCliente.length > 0,
        resultado: callResult,
        notas: notes
      });
      if (error) throw error;
      setSubmitSuccess(true);
    } catch (err) {
      console.error('Error saving call:', err);
      alert('Error al guardar el registro de la llamada.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const toggleOfferedProduct = (product: Product) => {
    setOfferedProducts(prev => {
      const exists = prev.find(p => p.productoId === product.id);
      if (exists) return prev.filter(p => p.productoId !== product.id);
      return [...prev, {
        productoId: product.id,
        nombre: product.nombre,
        sku: product.sku,
        precio: product.precio
      }];
    });
  };
  const toggleInterestedProduct = (product: SelectedProduct) => {
    setInterestedProducts(prev => {
      const exists = prev.find(p => p.productoId === product.productoId);
      if (exists) return prev.filter(p => p.productoId !== product.productoId);
      return [...prev, product];
    });
  };
  const addVencidoCliente = (product: Product) => {
    setVencidosCliente(prev => [...prev, {
      productoId: product.id,
      nombre: product.nombre,
      sku: product.sku,
      cantidad: vencidoQty,
      notas: vencidoNotes
    }]);
    setVencidoSearch('');
    setVencidoQty(1);
    setVencidoNotes('');
  };
  const removeVencidoCliente = (index: number) => {
    setVencidosCliente(prev => prev.filter((_, i) => i !== index));
  };
  // Filter inventory for search and flow
  const filteredInventory = inventory.filter(p => p.nombre.toLowerCase().includes(searchQuery.toLowerCase()) || p.sku.toLowerCase().includes(searchQuery.toLowerCase())).sort((a, b) => {
    if (flow === 'ofertas') {
      const aPromo = promotions.includes(a.id);
      const bPromo = promotions.includes(b.id);
      if (aPromo && !bPromo) return -1;
      if (!aPromo && bPromo) return 1;
    } else if (flow === 'alta_rotacion') {
      const aCount = topProducts[a.id] || 0;
      const bCount = topProducts[b.id] || 0;
      if (aCount !== bCount) return bCount - aCount;
    } else if (flow === 'vencimientos') {
      // Sort by longest expiration date first (nulls last)
      if (a.fechaVencimiento && b.fechaVencimiento) {
        return new Date(b.fechaVencimiento).getTime() - new Date(a.fechaVencimiento).getTime();
      }
      if (a.fechaVencimiento) return -1;
      if (b.fechaVencimiento) return 1;
    }
    return a.nombre.localeCompare(b.nombre);
  }).slice(0, 15);
  const filteredVencidoSearch = inventory.filter(p => p.nombre.toLowerCase().includes(vencidoSearch.toLowerCase()) || p.sku.toLowerCase().includes(vencidoSearch.toLowerCase())).slice(0, 5);
  if (submitSuccess) {
    return <div className="space-y-6" data-id="element-554" data-mp="1lh58j6v7yzb">
        <PageHeader title="Call Center" subtitle="Speech interactivo y gestión telefónica" icon={<PhoneIcon className="w-6 h-6" data-id="element-556" data-mp="3nti5np7dt6xn" />} data-id="element-555" data-mp="1omtjw7o6wtz" />
        <Card className="text-center py-16 animate-fade-in" data-id="element-557" data-mp="3nv1it6xqr1v7">
          <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-558" data-mp="3ss1qfma8ocof">
            <CheckCircleIcon className="w-10 h-10" data-id="element-559" data-mp="ke2u2jjcupae" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-560" data-mp="pce4avdkb1t5">
            ¡Llamada Registrada!
          </h2>
          <p className="text-slate-500 mb-8 max-w-md mx-auto" data-id="element-561" data-mp="alx57ioegnzf">
            El registro de la llamada con {selectedClient?.nombre} se ha
            guardado exitosamente en el sistema.
          </p>
          <Button onClick={resetForm} size="lg" data-id="element-562" data-mp="13akvxtrx45kd">
            Iniciar Nueva Llamada
          </Button>
        </Card>
      </div>;
  }
  return <div className="space-y-6 pb-20" data-id="element-563" data-mp="184f90ed0pmah">
      <PageHeader title="Call Center" subtitle="Speech interactivo y gestión telefónica" icon={<PhoneIcon className="w-6 h-6" data-id="element-565" data-mp="2nsn0nq1n6i8r" />} data-id="element-564" data-mp="2ss82q9c3z6pp" />

      <div className="flex space-x-1 bg-slate-100 p-1 rounded-xl w-fit mb-6" data-id="element-566" data-mp="2nru1ba50iv0n">
        <button onClick={() => setActiveTab('speech')} className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'speech' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'}`} data-id="element-567" data-mp="32hrih64iykgc">
          <PhoneIcon className="w-4 h-4 mr-2" data-id="element-568" data-mp="24wn3m0tt4oib" />
          Speech Interactivo
        </button>
        <button onClick={() => setActiveTab('historial')} className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'historial' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'}`} data-id="element-569" data-mp="2iw4vwq6bajwf">
          <HistoryIcon className="w-4 h-4 mr-2" data-id="element-570" data-mp="3q7votl93kyb1" />
          Historial de Llamadas
        </button>
      </div>

      {activeTab === 'historial' ? <Card data-id="element-571" data-mp="2rvt0n08ab757">
          <CardHeader title="Registro de Llamadas" data-id="element-572" data-mp="2rv9k9nya8qta" />
          <div className="overflow-x-auto" data-id="element-573" data-mp="2ry5jdflv9ljb">
            <table className="w-full text-sm text-left" data-id="element-574" data-mp="2wwgrnlwijnzf">
              <thead className="text-xs text-slate-500 bg-slate-50 border-b border-slate-200" data-id="element-575" data-mp="30x0nmm5coljz">
                <tr data-id="element-576" data-mp="30uo6gb7f2f7z">
                  <th className="px-4 py-3 font-medium" data-id="element-577" data-mp="2479twp4vv2f8">Fecha</th>
                  <th className="px-4 py-3 font-medium" data-id="element-578" data-mp="244ney4fnxkmz">Agente</th>
                  <th className="px-4 py-3 font-medium" data-id="element-579" data-mp="296mhjc3kwdiv">Cliente</th>
                  <th className="px-4 py-3 font-medium" data-id="element-580" data-mp="2d6wkefw2qnti">Flujo</th>
                  <th className="px-4 py-3 font-medium" data-id="element-581" data-mp="2d5li6kia1o4i">Resultado</th>
                  <th className="px-4 py-3 font-medium" data-id="element-582" data-mp="2hzpod1jpxs4j">Detalles</th>
                  <th className="px-4 py-3 font-medium text-center" data-id="element-583" data-mp="1lh1uoi4b0w4b">Ver</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-584" data-mp="1pgj23h3ak3c4">
                {isLoadingHistory ? <tr data-id="element-585" data-mp="1hx1gzzaef5dg">
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-id="element-586" data-mp="1hxjhx980a7rp">
                      <div className="flex justify-center mb-2" data-id="element-587" data-mp="18wv6mm9zylii">
                        <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" data-id="element-588" data-mp="18zh9w9puueux" />
                      </div>
                      Cargando historial...
                    </td>
                  </tr> : callsHistory.length === 0 ? <tr data-id="element-589" data-mp="u6xczqtat3lx">
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-id="element-590" data-mp="p9dx6mgycirv">
                      No hay registros de llamadas aún.
                    </td>
                  </tr> : callsHistory.map(call => <tr key={call.id} className="hover:bg-slate-50" data-id="element-591" data-mp="baxgq3tafdwj">
                      <td className="px-4 py-3 whitespace-nowrap" data-id="element-592" data-mp="2bcdv9mmpsdcn">
                        <div className="flex items-center text-slate-900" data-id="element-593" data-mp="2be8fszu2q3g7">
                          <CalendarIcon className="w-4 h-4 mr-2 text-slate-400" data-id="element-594" data-mp="2gap3jsxd2fsr" />
                          {format(new Date(call.fecha), 'd MMM yyyy, h:mm a', {
                    locale: es
                  })}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-900 font-medium" data-id="element-595" data-mp="1skl3fvjdsy6i">
                        {call.agente_nombre}
                      </td>
                      <td className="px-4 py-3" data-id="element-596" data-mp="1wkwlr1o9umet">
                        <p className="text-slate-900 font-medium" data-id="element-597" data-mp="1iohwmcdl4bd8">
                          {call.cliente_nombre}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-598" data-mp="1noe3qkhkwix0">
                          {call.cliente_zona}
                        </p>
                      </td>
                      <td className="px-4 py-3" data-id="element-599" data-mp="3hz9entg8ka2y">
                        <Badge variant="secondary" className="capitalize" data-id="element-600" data-mp="16d8lcl5g79oy">
                          {call.flujo_seleccionado.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="px-4 py-3" data-id="element-601" data-mp="1g2s3folc6zir">
                        <Badge variant={call.resultado === 'venta_realizada' ? 'success' : call.resultado === 'promesa_compra' || call.resultado === 'promesa_cobro' ? 'info' : call.resultado === 'seguimiento' ? 'warning' : 'danger'} data-id="element-602" data-mp="1g4lcfajf9vtx">
                          {call.resultado.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="px-4 py-3" data-id="element-603" data-mp="3vv7sgkuqo7n3">
                        <div className="text-xs space-y-1" data-id="element-604" data-mp="pcxldhs3r2cg">
                          {call.productos_interes?.length > 0 && <p className="text-emerald-600 font-medium" data-id="element-605" data-mp="tdr9oc9gij5v">
                              {call.productos_interes.length} prod. interesaron
                            </p>}
                          {call.tiene_productos_vencidos && <p className="text-rose-600 font-medium flex items-center" data-id="element-606" data-mp="18bs411uosmxw">
                              <AlertTriangleIcon className="w-3 h-3 mr-1" data-id="element-607" data-mp="189z1nyr3wovp" />
                              Reportó vencidos
                            </p>}
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-608" data-mp="1m3gdsyhgqzbp">
                        <button onClick={() => setSelectedCall(call)} className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Ver detalle" data-id="element-609" data-mp="1m5aw051codc3">
                          <EyeIcon className="w-4 h-4" data-id="element-610" data-mp="3gkzxy3w8r499" />
                        </button>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </Card> : <>
          {/* Progress Bar */}
          <div className="mb-6 sm:mb-8" data-id="element-611" data-mp="31npbjzi28dyr">
            <div className="flex items-center justify-between relative" data-id="element-612" data-mp="34iouyy193vv">
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-0.5 sm:h-1 bg-slate-200 rounded-full -z-10" data-id="element-613" data-mp="36lehhv9wv02"></div>
              <div className="absolute left-0 top-1/2 -translate-y-1/2 h-0.5 sm:h-1 bg-indigo-600 rounded-full -z-10 transition-all duration-300" style={{
            width: `${(currentStep - 1) / 6 * 100}%`
          }} data-id="element-614" data-mp="3pjtopdmw50ew"></div>

              {[1, 2, 3, 4, 5, 6, 7].map(step => <div key={step} className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[10px] sm:text-sm font-bold border-2 transition-colors ${step < currentStep ? 'bg-indigo-600 border-indigo-600 text-white' : step === currentStep ? 'bg-white border-indigo-600 text-indigo-600' : 'bg-white border-slate-300 text-slate-400'}`} data-id="element-615" data-mp="h3i6hmn4oiij">
                  {step < currentStep ? <CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-616" data-mp="94zb3s7smtbp" /> : step}
                </div>)}
            </div>
            <div className="flex justify-between mt-1.5 sm:mt-2 text-[10px] sm:text-xs font-medium text-slate-500 px-1" data-id="element-617" data-mp="3vc66v8t02ghz">
              <span data-id="element-618" data-mp="44tnqo8k3szf">Apertura</span>
              <span data-id="element-619" data-mp="43s0zmi6hi1v">Cierre</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-620" data-mp="2jwhh69od33dh">
            {/* Left Column: Script & Guidance */}
            <div className="lg:col-span-1 space-y-6" data-id="element-621" data-mp="2own3rbfrxzop">
              <Card className="bg-indigo-50 border-indigo-100 sticky top-6" data-id="element-622" data-mp="2hc2qlidtot8j">
                <CardHeader title="Guía de Llamada" data-id="element-623" data-mp="2he5qcu4s20qg" />
                <div className="mt-4 space-y-4" data-id="element-624" data-mp="2cc6oxpslldw5">
                  {currentStep === 1 && <div className="animate-fade-in" data-id="element-625" data-mp="2qby6u1m8jeff">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-626" data-mp="2qan13w98bwlo">
                        Lee el siguiente texto:
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-627" data-mp="1xp52l99mzx0o">
                        "Hola, ¿hablo con el encargado? Le habla{' '}
                        <span className="font-bold text-indigo-700 not-italic" data-id="element-628" data-mp="27f6q9zy2vdf2">
                          {user?.nombre || 'su servidor'}
                        </span>{' '}
                        de Miladys Rodríguez y Asociados. Le llamo rápidamente
                        porque estamos viendo como ayudar a farmacias como la
                        suya a comprar mejor y mejorar la rotación de sus
                        productos."
                      </div>
                    </div>}

                  {currentStep === 2 && <div className="animate-fade-in" data-id="element-629" data-mp="g4ea5qe9kh28">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-630" data-mp="g565d03y8crz">
                        Si responde que SÍ:
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-631" data-mp="p52suyelr5lg">
                        "Perfecto, gracias. Para ayudarle mejor, le hago una
                        consulta rápida:
                        <br data-id="element-632" data-mp="z1xnh67r8lug" />
                        <br data-id="element-633" data-mp="z162sqfp48iy" />
                        <strong data-id="element-634" data-mp="u4fclaa6fwjt">¿Le interesa más ahora mismo:</strong>
                        <br data-id="element-635" data-mp="u22w71k2hu8v" />
                        • ver productos en oferta,
                        <br data-id="element-636" data-mp="30v5bd41fobry" />
                        • productos que se están vendiendo rápido,
                        <br data-id="element-637" data-mp="2vx3i5qaamw2r" />
                        • revisar opciones para evitar pérdidas por vencimiento,
                        <br data-id="element-638" data-mp="1be9rlawry01a" />• o validar productos que usted ya tenga en
                        riesgo?"
                      </div>
                    </div>}

                  {currentStep === 3 && <div className="animate-fade-in" data-id="element-639" data-mp="11lu8ga9qr5et">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-640" data-mp="16lgn8o57wtpq">
                        Registro de Vencimientos:
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-641" data-mp="xltlu2chnnwo">
                        "Entiendo.{' '}
                        <strong data-id="element-642" data-mp="iqnf1d5kjl2a">
                          ¿Cuáles productos tiene en esa condición?
                        </strong>
                        "
                        <br data-id="element-643" data-mp="2d2lq7knfur0t" />
                        <br data-id="element-644" data-mp="2d3d7rrk7ao83" />
                        <span className="text-xs text-rose-600 font-bold not-italic block mt-2" data-id="element-645" data-mp="2d10w1w51da3h">
                          🔴 IMPORTANTE: Solo registrar, NO ofrecer devolución
                          en esta llamada.
                        </span>
                        <br data-id="element-646" data-mp="2bkmyko21nas3" />
                        "Perfecto, tomo nota de esos productos. Eso le puede
                        estar generando pérdidas si no se mueven a tiempo. En
                        base a eso, le puedo ayudar con opciones que se vendan
                        más rápido o complementen esos productos."
                      </div>
                    </div>}

                  {currentStep === 4 && <div className="animate-fade-in" data-id="element-647" data-mp="2ueiir5u04v64">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-648" data-mp="2pk4uroho5qbh">
                        Presentar Opciones:
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-649" data-mp="3i1qm3i7vdu5i">
                        "Le comento, tengo algunas opciones que le pueden
                        funcionar:"
                        <br data-id="element-650" data-mp="zt5vo6xl7eqf" />
                        <br data-id="element-651" data-mp="ztyu8kme6xxh" />
                        <ul className="list-disc pl-4 space-y-1 mt-2" data-id="element-652" data-mp="zuqptvfzovnr">
                          {offeredProducts.length === 0 ? <li className="text-slate-400" data-id="element-653" data-mp="18sswormemiiy">
                              Selecciona productos a la derecha...
                            </li> : offeredProducts.map(p => <li key={p.productoId} data-id="element-654" data-mp="1ip5qdphy8wdp">
                                <strong data-id="element-655" data-mp="1npjq8k9ed00n">{p.nombre}</strong>
                              </li>)}
                        </ul>
                      </div>
                    </div>}

                  {currentStep === 5 && <div className="animate-fade-in" data-id="element-656" data-mp="21ij1ga25c82e">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-657" data-mp="21fn68thonxhn">
                        Medir Interés:
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-658" data-mp="1stc14g53mjkw">
                        "<strong data-id="element-659" data-mp="1nu95fmb0dppl">¿Cuál de estos le interesa más?</strong>
                        <br data-id="element-660" data-mp="22k6q3s9qbxa1" />o ¿Prefiere que le recomiende el que más salida
                        está teniendo en su zona?"
                      </div>
                    </div>}

                  {currentStep === 6 && <div className="animate-fade-in" data-id="element-661" data-mp="19vhwsfex0w8u">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-662" data-mp="14wyauf0bnkgh">
                        Ajuste Dinámico (Si duda):
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-663" data-mp="1jrcn7wpdrvm6">
                        "Puedo ajustarle opciones:
                        <br data-id="element-664" data-mp="2v716oe3feci8" />
                        • más económicas
                        <br data-id="element-665" data-mp="2qaaqzrvj9ixc" />
                        • con mayor ganancia
                        <br data-id="element-666" data-mp="2xtaarymwpch8" />
                        • de más rotación"
                        <br data-id="element-667" data-mp="2xv3lpq4jyiv1" />
                        <br data-id="element-668" data-mp="32ru10b8j9afe" />
                        <span className="text-xs text-indigo-600 font-bold not-italic block" data-id="element-669" data-mp="32t4zbvj7lpz4">
                          Vuelve al paso 4 si necesitas buscar otros productos.
                        </span>
                      </div>
                    </div>}

                  {currentStep === 7 && <div className="animate-fade-in" data-id="element-670" data-mp="3gk1pzgtkrx3j">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-671" data-mp="3lj4kw8fucmxs">
                        Cierre:
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-672" data-mp="37qor7y8ty19j">
                        "
                        <strong data-id="element-673" data-mp="3d8gg7t8wnp9">
                          ¿Le gustaría probar con este producto en su próximo
                          pedido?
                        </strong>
                        "<br data-id="element-674" data-mp="85jfz9k2q7ha" />
                        <br data-id="element-675" data-mp="i0755v0px40a" />
                        <span className="text-xs text-slate-500 not-italic block" data-id="element-676" data-mp="hydxqdia6fr8">
                          Registra el resultado final de la llamada y guarda.
                        </span>
                      </div>
                    </div>}
                </div>
              </Card>
            </div>

            {/* Right Column: Interactive Workspace */}
            <div className="lg:col-span-2" data-id="element-677" data-mp="3v6gybrr1px2n">
              <Card className="min-h-[500px] flex flex-col" data-id="element-678" data-mp="3v7ie29j0b7qp">
                <div className="flex-1" data-id="element-679" data-mp="3qefpygx00qhw">
                  {/* STEP 1: APERTURA */}
                  {currentStep === 1 && <div className="space-y-6 animate-fade-in" data-id="element-680" data-mp="3me5a8cjemlpu">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-681" data-mp="3hflp2ecmxdyj">
                        <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center" data-id="element-682" data-mp="3cfzao1l2lro5">
                          <UserIcon className="w-5 h-5" data-id="element-683" data-mp="37hxt5l3am0es" />
                        </div>
                        <div data-id="element-684" data-mp="2xbo5va7dlv1x">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-685" data-mp="2xcfwe6m492lc">
                            1. Apertura de Llamada
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-686" data-mp="37alxim03m4p5">
                            Selecciona el cliente a contactar
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4" data-id="element-687" data-mp="2enj3o76dai58">
                        <SearchableSelect label="Buscar Cliente / Farmacia" options={clients.map(c => ({
                    value: c.id,
                    label: `${c.nombre} - ${c.zona}`
                  }))} value={selectedClientId} onChange={setSelectedClientId} placeholder="Escribe para buscar..." data-id="element-688" data-mp="2jlawr4ir0ab0" />

                        {selectedClient && <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3" data-id="element-689" data-mp="3f2b70s0k3jtv">
                            <div className="flex items-start justify-between" data-id="element-690" data-mp="3b0zrjfbeceze">
                              <div data-id="element-691" data-mp="3ozpo8tub0cn8">
                                <h4 className="font-bold text-slate-900 text-lg" data-id="element-692" data-mp="3p0r0v2qyx772">
                                  {selectedClient.nombre}
                                </h4>
                                <p className="text-sm text-slate-500 flex items-center gap-1 mt-1" data-id="element-693" data-mp="1toq21hshm6j">
                                  <MapPinIcon className="w-4 h-4" data-id="element-694" data-mp="3txr7berqcbyp" />{' '}
                                  {selectedClient.zona} |{' '}
                                  {selectedClient.ciudad}
                                </p>
                              </div>
                              <Badge variant="info" data-id="element-695" data-mp="lih8cfj4zmv5">
                                {selectedClient.codigo}
                              </Badge>
                            </div>
                            <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-200" data-id="element-696" data-mp="ght8r5gudxpb">
                              <div data-id="element-697" data-mp="103l90fk1juxv">
                                <p className="text-xs text-slate-500" data-id="element-698" data-mp="147az59gd6kl0">
                                  Teléfono
                                </p>
                                <p className="font-medium text-slate-900" data-id="element-699" data-mp="q9cl655ygq61">
                                  {selectedClient.telefono || 'No registrado'}
                                </p>
                              </div>
                              <div data-id="element-700" data-mp="l7x0wfblqnop">
                                <p className="text-xs text-slate-500" data-id="element-701" data-mp="l8yofj9t12nb">
                                  Contacto
                                </p>
                                <p className="font-medium text-slate-900" data-id="element-702" data-mp="cfv2snp09jhl">
                                  {selectedClient.contacto || 'No registrado'}
                                </p>
                              </div>
                            </div>
                          </div>}
                      </div>

                      {selectedClient && <div className="pt-6 grid grid-cols-3 gap-3" data-id="element-703" data-mp="3ukz26yeylwoh">
                          <Button variant="danger" className="w-full" onClick={() => {
                    setCallResult('no_contesto');
                    setCurrentStep(7);
                  }} data-id="element-704" data-mp="3cuydy2givuf">
                            No Contestó
                          </Button>
                          <Button variant="warning" className="w-full" onClick={() => {
                    setCallResult('ocupado');
                    setCurrentStep(7);
                  }} data-id="element-705" data-mp="m9coyc5kkpp3">
                            Ocupado
                          </Button>
                          <Button variant="primary" className="w-full" onClick={handleNext} data-id="element-706" data-mp="14zlyy87lllrc">
                            Cliente Disponible
                          </Button>
                        </div>}
                    </div>}

                  {/* STEP 2: DETECCIÓN */}
                  {currentStep === 2 && <div className="space-y-6 animate-fade-in" data-id="element-707" data-mp="388xfikekezah">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-708" data-mp="348vjorvih034">
                        <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center" data-id="element-709" data-mp="2zhne66xaxyv8">
                          <SearchIcon className="w-5 h-5" data-id="element-710" data-mp="2uhra67olu7fo" />
                        </div>
                        <div data-id="element-711" data-mp="3ryg5rjmyodgm">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-712" data-mp="3rzr9xkkbjn89">
                            2. Detección de Interés
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-713" data-mp="9xu60puhvi4i">
                            ¿Qué le interesa más al cliente?
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-714" data-mp="14z7sg3g2mo8">
                        <button onClick={() => setFlow('ofertas')} className={`p-5 rounded-xl border-2 text-left transition-all ${flow === 'ofertas' ? 'border-indigo-600 bg-indigo-50' : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'}`} data-id="element-715" data-mp="573z386f777p">
                          <TagIcon className={`w-8 h-8 mb-3 ${flow === 'ofertas' ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-716" data-mp="3edvkzbxunnpp" />
                          <h4 className="font-bold text-slate-900" data-id="element-717" data-mp="1ckxmp0lb02xd">
                            Productos en Oferta
                          </h4>
                          <p className="text-xs text-slate-500 mt-1" data-id="element-718" data-mp="1glzl5f7ecaya">
                            Promociones y descuentos actuales
                          </p>
                        </button>

                        <button onClick={() => setFlow('alta_rotacion')} className={`p-5 rounded-xl border-2 text-left transition-all ${flow === 'alta_rotacion' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 hover:border-emerald-300 hover:bg-slate-50'}`} data-id="element-719" data-mp="2x52q05a8xh23">
                          <TrendingUpIcon className={`w-8 h-8 mb-3 ${flow === 'alta_rotacion' ? 'text-emerald-600' : 'text-slate-400'}`} data-id="element-720" data-mp="3ftzj45twq3iq" />
                          <h4 className="font-bold text-slate-900" data-id="element-721" data-mp="3ounpced26zl2">
                            Alta Rotación
                          </h4>
                          <p className="text-xs text-slate-500 mt-1" data-id="element-722" data-mp="1zg96gaoq26xh">
                            Lo que más se está vendiendo
                          </p>
                        </button>

                        <button onClick={() => setFlow('vencimientos')} className={`p-5 rounded-xl border-2 text-left transition-all ${flow === 'vencimientos' ? 'border-amber-600 bg-amber-50' : 'border-slate-200 hover:border-amber-300 hover:bg-slate-50'}`} data-id="element-723" data-mp="1wu03itgz9d02">
                          <ClockIcon className={`w-8 h-8 mb-3 ${flow === 'vencimientos' ? 'text-amber-600' : 'text-slate-400'}`} data-id="element-724" data-mp="2asq9kylryn0k" />
                          <h4 className="font-bold text-slate-900" data-id="element-725" data-mp="1i6otiw0g2122">
                            Evitar Vencimientos
                          </h4>
                          <p className="text-xs text-slate-500 mt-1" data-id="element-726" data-mp="1rvou2julh4id">
                            Opciones con fecha larga
                          </p>
                        </button>

                        <button onClick={() => setFlow('validar_riesgo')} className={`p-5 rounded-xl border-2 text-left transition-all ${flow === 'validar_riesgo' ? 'border-rose-600 bg-rose-50' : 'border-slate-200 hover:border-rose-300 hover:bg-slate-50'}`} data-id="element-727" data-mp="zfnsm2frjvfv">
                          <AlertTriangleIcon className={`w-8 h-8 mb-3 ${flow === 'validar_riesgo' ? 'text-rose-600' : 'text-slate-400'}`} data-id="element-728" data-mp="hwxh21r7s0zl" />
                          <h4 className="font-bold text-slate-900" data-id="element-729" data-mp="8xip0xveficz">
                            Validar Riesgo
                          </h4>
                          <p className="text-xs text-slate-500 mt-1" data-id="element-730" data-mp="44h8klfjnara">
                            Productos del cliente por vencer
                          </p>
                        </button>
                      </div>
                    </div>}

                  {/* STEP 3: VENCIDOS DEL CLIENTE (Only if validar_riesgo) */}
                  {currentStep === 3 && <div className="space-y-6 animate-fade-in" data-id="element-731" data-mp="3hdvqz3ela0ct">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-732" data-mp="36rp9k3wfhevf">
                        <div className="w-10 h-10 bg-rose-100 text-rose-600 rounded-xl flex items-center justify-center" data-id="element-733" data-mp="3brboqivth56v">
                          <AlertTriangleIcon className="w-5 h-5" data-id="element-734" data-mp="2xslpawliytfc" />
                        </div>
                        <div data-id="element-735" data-mp="32r6iic2d0cua">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-736" data-mp="32shmoczpvmlx">
                            3. Registro de Vencimientos
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-737" data-mp="3ug9lloiifh5c">
                            Registra los productos que el cliente menciona
                          </p>
                        </div>
                      </div>

                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4" data-id="element-738" data-mp="3lnemlcjmyho0">
                        <div className="relative" data-id="element-739" data-mp="d7cvjcgnmcyx">
                          <SearchIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-740" data-mp="i0ebzowiekkm" />
                          <input type="text" placeholder="Buscar producto en inventario..." value={vencidoSearch} onChange={e => setVencidoSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none" data-id="element-741" data-mp="rdn8k454efy5" />
                        </div>

                        {vencidoSearch && filteredVencidoSearch.length > 0 && <div className="bg-white border border-slate-200 rounded-lg shadow-sm max-h-48 overflow-y-auto" data-id="element-742" data-mp="iejlkp2u1ged">
                            {filteredVencidoSearch.map(p => <div key={p.id} className="flex items-center justify-between p-3 border-b border-slate-100 last:border-0 hover:bg-slate-50" data-id="element-743" data-mp="1tsngpyl5pal0">
                                <div data-id="element-744" data-mp="3j8hcynrm57d2">
                                  <p className="font-medium text-sm text-slate-900" data-id="element-745" data-mp="3nbnr0hkyw2u3">
                                    {p.nombre}
                                  </p>
                                  <p className="text-xs text-slate-500" data-id="element-746" data-mp="3s9fixblm3oya">
                                    SKU: {p.sku}
                                  </p>
                                </div>
                                <Button size="sm" variant="secondary" onClick={() => {
                        setVencidoSearch(p.nombre);
                        // We don't add immediately, let them set qty/notes
                      }} data-id="element-747" data-mp="5y0g4xcfm7si">
                                  Seleccionar
                                </Button>
                              </div>)}
                          </div>}

                        <div className="grid grid-cols-3 gap-3" data-id="element-748" data-mp="29cudvuri2dy5">
                          <div className="col-span-1" data-id="element-749" data-mp="29b9enru8q6ty">
                            <Input type="number" label="Cantidad" value={vencidoQty} onChange={e => setVencidoQty(Number(e.target.value))} min={1} data-id="element-750" data-mp="24cpx03mojh7e" />
                          </div>
                          <div className="col-span-2" data-id="element-751" data-mp="2n2ea0hqkscyh">
                            <Input label="Notas (Lote, Fecha aprox)" value={vencidoNotes} onChange={e => setVencidoNotes(e.target.value)} placeholder="Ej: Vence en 2 meses" data-id="element-752" data-mp="2j2nlkkoiu8x4" />
                          </div>
                        </div>

                        <Button className="w-full" disabled={!vencidoSearch} onClick={() => {
                    const prod = inventory.find(p => p.nombre === vencidoSearch || p.sku === vencidoSearch);
                    if (prod) {
                      addVencidoCliente(prod);
                    } else {
                      // Allow adding custom text if not in inventory
                      setVencidosCliente(prev => [...prev, {
                        productoId: 'custom',
                        nombre: vencidoSearch,
                        sku: 'N/A',
                        cantidad: vencidoQty,
                        notas: vencidoNotes
                      }]);
                      setVencidoSearch('');
                      setVencidoQty(1);
                      setVencidoNotes('');
                    }
                  }} data-id="element-753" data-mp="v4mpxcmr5nsu">
                          <PlusIcon className="w-4 h-4 mr-2" data-id="element-754" data-mp="1mcxse092xnrm" /> Agregar a la
                          lista
                        </Button>
                      </div>

                      {vencidosCliente.length > 0 && <div className="space-y-2" data-id="element-755" data-mp="1vb9r6pk3nrv5">
                          <h4 className="text-sm font-bold text-slate-700" data-id="element-756" data-mp="203b0q3pvv4hh">
                            Productos Registrados:
                          </h4>
                          {vencidosCliente.map((v, i) => <div key={i} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200" data-id="element-757" data-mp="17mgpsdkpkrt5" data-mpi={i}>
                              <div data-id="element-758" data-mp="25yjklhg11h7y">
                                <p className="font-medium text-sm text-slate-900" data-id="element-759" data-mp="2knz9sszwtelj">
                                  {v.nombre}{' '}
                                  <span className="text-slate-500 font-normal" data-id="element-760" data-mp="2fomctffbqr58">
                                    x{v.cantidad}
                                  </span>
                                </p>
                                {v.notas && <p className="text-xs text-slate-500" data-id="element-761" data-mp="1n2jq9zzku1l7">
                                    {v.notas}
                                  </p>}
                              </div>
                              <button onClick={() => removeVencidoCliente(i)} className="text-rose-500 hover:bg-rose-50 p-1.5 rounded-lg" data-id="element-762" data-mp="3ceveh6btgcxq">
                                <TrashIcon className="w-4 h-4" data-id="element-763" data-mp="3fw9mxw6b009j" />
                              </button>
                            </div>)}
                        </div>}
                    </div>}

                  {/* STEP 4: MOSTRAR PRODUCTOS */}
                  {currentStep === 4 && <div className="space-y-6 animate-fade-in" data-id="element-764" data-mp="2ig0ut7drtc1">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-765" data-mp="2iy2648wgxqt">
                        <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center" data-id="element-766" data-mp="7ghh76df9ejt">
                          <PackageIcon className="w-5 h-5" data-id="element-767" data-mp="3twu30kku3hzc" />
                        </div>
                        <div data-id="element-768" data-mp="3tuhe1ngns7cs">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-769" data-mp="lgilywwt564k">
                            4. Selección de Productos
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-770" data-mp="q9lf53xoadk8">
                            Elige los productos a ofrecer al cliente
                          </p>
                        </div>
                      </div>

                      <div className="relative" data-id="element-771" data-mp="2oqdpud8wrgfg">
                        <SearchIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-772" data-mp="2or5m7qabxi6s" />
                        <input type="text" placeholder="Buscar en inventario..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none" data-id="element-773" data-mp="2jtdnacb9ovt3" />
                      </div>

                      <div className="bg-slate-50 rounded-xl border border-slate-200 h-[300px] overflow-y-auto p-2" data-id="element-774" data-mp="416hrqshtr8x">
                        {isLoadingInventory ? <div className="flex items-center justify-center h-full" data-id="element-775" data-mp="7iu4x82krzb4">
                            <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" data-id="element-776" data-mp="bjnue5vo1m64" />
                          </div> : filteredInventory.length === 0 ? <div className="flex flex-col items-center justify-center h-full text-slate-400" data-id="element-777" data-mp="lidcnzrh58mw">
                            <PackageIcon className="w-8 h-8 mb-2 opacity-50" data-id="element-778" data-mp="qg5833rbvcvu" />
                            <p className="text-sm" data-id="element-779" data-mp="ui9upijcz98z">
                              No se encontraron productos
                            </p>
                          </div> : <div className="space-y-2" data-id="element-780" data-mp="148usj3r7k9qu">
                            {filteredInventory.map(product => {
                      const isSelected = offeredProducts.some(p => p.productoId === product.id);
                      return <div key={product.id} onClick={() => toggleOfferedProduct(product)} className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-200 hover:border-indigo-300'}`} data-id="element-781" data-mp="oj52y5wd1wkz">
                                  <div className="flex-1 min-w-0 pr-4" data-id="element-782" data-mp="12aa9lry5bp4t">
                                    <p className={`font-medium text-sm truncate ${isSelected ? 'text-indigo-900' : 'text-slate-900'}`} data-id="element-783" data-mp="12c4i26s8dos0">
                                      {product.nombre}
                                    </p>
                                    <div className="flex items-center gap-3 mt-1" data-id="element-784" data-mp="3j0ih15zv43pp">
                                      <span className="text-xs text-slate-500" data-id="element-785" data-mp="3e1f99dshzje1">
                                        SKU: {product.sku}
                                      </span>
                                      <span className="text-xs font-medium text-emerald-600" data-id="element-786" data-mp="1yg3ju2mr7ya0">
                                        RD$ {product.precio}
                                      </span>
                                      <span className={`text-xs ${product.stock > product.stockMinimo ? 'text-slate-500' : 'text-rose-500'}`} data-id="element-787" data-mp="1op8weuxrusmz">
                                        Stock: {product.stock}
                                      </span>
                                    </div>
                                  </div>
                                  <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-300'}`} data-id="element-788" data-mp="15s7rfa47wsj1">
                                    {isSelected && <CheckCircleIcon className="w-3 h-3" data-id="element-789" data-mp="1n0ypkkpyg68k" />}
                                  </div>
                                </div>;
                    })}
                          </div>}
                      </div>

                      {offeredProducts.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-790" data-mp="1469uqqvx3c74">
                          {offeredProducts.map(p => <span key={p.productoId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium" data-id="element-791" data-mp="v7ofp5pnmfb3">
                              {p.nombre}
                              <button onClick={() => setOfferedProducts(prev => prev.filter(x => x.productoId !== p.productoId))} data-id="element-792" data-mp="2kn5s7p2p7oia">
                                <XIcon className="w-3 h-3 hover:text-indigo-900" data-id="element-793" data-mp="36vkwiu30er9f" />
                              </button>
                            </span>)}
                        </div>}
                    </div>}

                  {/* STEP 5: INTERÉS DEL CLIENTE */}
                  {currentStep === 5 && <div className="space-y-6 animate-fade-in" data-id="element-794" data-mp="2n72f4yo8wi5a">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-795" data-mp="2n8n66ebcpiye">
                        <div className="w-10 h-10 bg-amber-100 text-amber-600 rounded-xl flex items-center justify-center" data-id="element-796" data-mp="3ukmbl06cl0no">
                          <CheckCircleIcon className="w-5 h-5" data-id="element-797" data-mp="tiwdue87xsud" />
                        </div>
                        <div data-id="element-798" data-mp="tl8v0p65jz6d">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-799" data-mp="yiitsvffln6v">
                            5. Interés del Cliente
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-800" data-mp="jr0e6yqhhuo1">
                            ¿Cuáles productos le interesaron?
                          </p>
                        </div>
                      </div>

                      {offeredProducts.length === 0 ? <div className="text-center py-8 text-slate-500" data-id="element-801" data-mp="ft2ozvwpr8kn">
                          No se ofrecieron productos en el paso anterior.
                        </div> : <div className="space-y-3" data-id="element-802" data-mp="5ttvt59o9xzc">
                          {offeredProducts.map(product => {
                    const isInterested = interestedProducts.some(p => p.productoId === product.productoId);
                    return <div key={product.productoId} onClick={() => toggleInterestedProduct(product)} className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${isInterested ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-amber-300'}`} data-id="element-803" data-mp="3fqaohjmlbti6">
                                <div data-id="element-804" data-mp="39ni45v9at5l">
                                  <p className={`font-bold ${isInterested ? 'text-amber-900' : 'text-slate-900'}`} data-id="element-805" data-mp="38m9e81pqir1">
                                    {product.nombre}
                                  </p>
                                  <p className="text-sm text-slate-500" data-id="element-806" data-mp="2j0tb0ihd935x">
                                    RD$ {product.precio}
                                  </p>
                                </div>
                                <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${isInterested ? 'bg-amber-500 border-amber-500 text-white' : 'border-slate-300'}`} data-id="element-807" data-mp="hzd9cr01uad5">
                                  {isInterested && <CheckCircleIcon className="w-4 h-4" data-id="element-808" data-mp="vvgza83w3wjt" />}
                                </div>
                              </div>;
                  })}
                        </div>}
                    </div>}

                  {/* STEP 6: AJUSTE */}
                  {currentStep === 6 && <div className="space-y-6 animate-fade-in" data-id="element-809" data-mp="1jn5xu67b1h97">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-810" data-mp="1jopfo18m00d3">
                        <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center" data-id="element-811" data-mp="1omqv8c6tthjt">
                          <TrendingUpIcon className="w-5 h-5" data-id="element-812" data-mp="1yo32lvzt5gr5" />
                        </div>
                        <div data-id="element-813" data-mp="1tt5selqsl1co">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-814" data-mp="1orq5sp8yums6">
                            6. Ajuste de Oferta
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-815" data-mp="1ks7mtz0pglrz">
                            Si el cliente dudó, ¿qué busca?
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3" data-id="element-816" data-mp="2cjnisjo5jkuv">
                        <Button variant="secondary" className="justify-start h-12" onClick={() => {
                    setSearchQuery('economico');
                    setCurrentStep(4);
                  }} data-id="element-817" data-mp="27pst8zlv8bnv">
                          <DollarSignIcon className="w-4 h-4 mr-3 text-emerald-600" data-id="element-818" data-mp="2vec0dbt6iied" />{' '}
                          Buscar opciones más económicas
                        </Button>
                        <Button variant="secondary" className="justify-start h-12" onClick={() => {
                    setSearchQuery('ganancia');
                    setCurrentStep(4);
                  }} data-id="element-819" data-mp="2kz311vxgoez5">
                          <TrendingUpIcon className="w-4 h-4 mr-3 text-blue-600" data-id="element-820" data-mp="33nzw420onbif" />{' '}
                          Buscar opciones con mayor margen
                        </Button>
                        <Button variant="secondary" className="justify-start h-12" onClick={() => {
                    setSearchQuery('rotacion');
                    setCurrentStep(4);
                  }} data-id="element-821" data-mp="2puie4lnna6ui">
                          <ClockIcon className="w-4 h-4 mr-3 text-amber-600" data-id="element-822" data-mp="1az8lg8nebkkm" />{' '}
                          Buscar opciones de alta rotación
                        </Button>
                      </div>

                      <div className="mt-8 p-4 bg-slate-50 rounded-xl border border-slate-200 text-center" data-id="element-823" data-mp="1kpuypw7n3z05">
                        <p className="text-sm text-slate-600 mb-3" data-id="element-824" data-mp="1fpysdqbgzvhf">
                          Si el cliente ya está convencido o no desea más
                          opciones:
                        </p>
                        <Button onClick={handleNext} className="w-full" data-id="element-825" data-mp="n2cn8dmlgisg">
                          Ir al Cierre
                        </Button>
                      </div>
                    </div>}

                  {/* STEP 7: CIERRE Y REGISTRO */}
                  {currentStep === 7 && <div className="space-y-6 animate-fade-in" data-id="element-826" data-mp="2dco1sl8i5adb">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-827" data-mp="28f5zhdozptea">
                        <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center" data-id="element-828" data-mp="2ficjg6vsshor">
                          <SaveIcon className="w-5 h-5" data-id="element-829" data-mp="2kg5sr8sedrt9" />
                        </div>
                        <div data-id="element-830" data-mp="2pg0iva4cmdd9">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-831" data-mp="2pgtepg1rrigm">
                            7. Cierre y Registro
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-832" data-mp="1ws4my7mlsphj">
                            Guarda el resultado de la llamada
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4" data-id="element-833" data-mp="26piufhqju3xp">
                        <div data-id="element-834" data-mp="26nxwdi5102v3">
                          <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-835" data-mp="2bh0tg090hug1">
                            Resultado de la Llamada *
                          </label>
                          <div className="grid grid-cols-2 gap-3" data-id="element-836" data-mp="1j07ws94hwpns">
                            <button onClick={() => setCallResult('venta_realizada')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'venta_realizada' ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300'}`} data-id="element-837" data-mp="1iyddt0csb7mc">
                              Venta Realizada
                            </button>
                            <button onClick={() => setCallResult('promesa_compra')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'promesa_compra' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300'}`} data-id="element-838" data-mp="3dd6hlz228j9y">
                              Promesa de Compra
                            </button>
                            <button onClick={() => setCallResult('promesa_cobro')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'promesa_cobro' ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-300'}`} data-id="element-839" data-mp="2pn2io4zth7pa">
                              Promesa de Cobro
                            </button>
                            <button onClick={() => setCallResult('seguimiento')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'seguimiento' ? 'bg-amber-50 border-amber-500 text-amber-700' : 'bg-white border-slate-200 text-slate-600 hover:border-amber-300'}`} data-id="element-840" data-mp="3urzba9j20c1l">
                              Requiere Seguimiento
                            </button>
                            <button onClick={() => setCallResult('no_interesado')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'no_interesado' ? 'bg-rose-50 border-rose-500 text-rose-700' : 'bg-white border-slate-200 text-slate-600 hover:border-rose-300'}`} data-id="element-841" data-mp="3gs6p8rih2jzy">
                              No Interesado
                            </button>
                            <button onClick={() => setCallResult('no_contesto')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'no_contesto' ? 'bg-slate-100 border-slate-400 text-slate-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-842" data-mp="3283yutqreudw">
                              No Contestó
                            </button>
                            <button onClick={() => setCallResult('ocupado')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'ocupado' ? 'bg-slate-100 border-slate-400 text-slate-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-843" data-mp="3on8j2srlemu">
                              Ocupado
                            </button>
                          </div>
                        </div>

                        <Textarea label="Notas Adicionales" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Comentarios sobre la llamada, objeciones, o detalles para el seguimiento..." rows={4} data-id="element-844" data-mp="g4r8jt8yp7wq" />

                        {/* Summary Box */}
                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-sm" data-id="element-845" data-mp="34yo9rwsnhxkh">
                          <h4 className="font-bold text-slate-900 mb-2" data-id="element-846" data-mp="30x2te1gwr15j">
                            Resumen a Guardar:
                          </h4>
                          <ul className="space-y-1 text-slate-600" data-id="element-847" data-mp="2qrkzqvv12jgb">
                            <li data-id="element-848" data-mp="2vk4cxrpszckl">
                              • <strong data-id="element-849" data-mp="2vlfj1y6q0wev">Cliente:</strong>{' '}
                              {selectedClient?.nombre}
                            </li>
                            <li data-id="element-850" data-mp="2ltt69xeqipa0">
                              • <strong data-id="element-851" data-mp="2luuk29n4xpvf">Flujo:</strong> {flow || 'N/A'}
                            </li>
                            <li data-id="element-852" data-mp="39kylqbgrjfjs">
                              • <strong data-id="element-853" data-mp="39hsyl1rp2nqp">Ofrecidos:</strong>{' '}
                              {offeredProducts.length} productos
                            </li>
                            <li data-id="element-854" data-mp="2zko84pqtg643">
                              • <strong data-id="element-855" data-mp="2znkaqrdlziyv">Interesaron:</strong>{' '}
                              {interestedProducts.length} productos
                            </li>
                            <li data-id="element-856" data-mp="1o5j67nyieblj">
                              • <strong data-id="element-857" data-mp="1o8ng91yno1k4">Vencimientos reportados:</strong>{' '}
                              {vencidosCliente.length}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>}
                </div>

                {/* Navigation Footer */}
                <div className="pt-6 mt-6 border-t border-slate-100 flex items-center justify-between" data-id="element-858" data-mp="3m17u5vnpbgqg">
                  <Button variant="secondary" onClick={handleBack} disabled={currentStep === 1 || isSubmitting} data-id="element-859" data-mp="3h2dc4vxk5w7e">
                    <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-860" data-mp="381f84dn4doot" /> Atrás
                  </Button>

                  {currentStep < 7 ? <Button onClick={handleNext} disabled={currentStep === 1 && !selectedClient || currentStep === 2 && !flow} data-id="element-861" data-mp="335hr05418eaz">
                      Siguiente <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-862" data-mp="wdgyz5gyi9pz" />
                    </Button> : <Button onClick={handleSaveCall} disabled={!callResult || isSubmitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-863" data-mp="13udnacoerhxr">
                      {isSubmitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" data-id="element-864" data-mp="1ikkxy686bwnw" /> : <SaveIcon className="w-4 h-4 mr-2" data-id="element-865" data-mp="pw5mfto93whb" />}
                      Guardar Llamada
                    </Button>}
                </div>
              </Card>
            </div>
          </div>
        </>}

      {/* Detail Modal */}
      <Modal isOpen={!!selectedCall} onClose={() => setSelectedCall(null)} title="Detalle de Llamada" size="lg" data-id="element-866" data-mp="26b75lwe9nexd">
        {selectedCall && <ModalBody data-id="element-867" data-mp="2bbuxekabjzsn">
            <div className="space-y-6" data-id="element-868" data-mp="28utqul5eocyu">
              {/* Client & Call Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-869" data-mp="24trr837ktywc">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-id="element-870" data-mp="24vkxhhea2h3t">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-871" data-mp="2iv46ecewxwh6">Cliente</p>
                  <p className="font-bold text-slate-900 text-lg" data-id="element-872" data-mp="2du6n1wi62we3">
                    {selectedCall.cliente_nombre}
                  </p>
                  <div className="flex items-center gap-2 mt-1 text-sm text-slate-500" data-id="element-873" data-mp="2rsejm3bajvoq">
                    <MapPinIcon className="w-3.5 h-3.5" data-id="element-874" data-mp="2ns4a4goain54" />
                    {selectedCall.cliente_zona}
                  </div>
                  {selectedCall.cliente_telefono && <div className="flex items-center gap-2 mt-1 text-sm text-slate-500" data-id="element-875" data-mp="2wm8ymtfybmhh">
                      <PhoneIcon className="w-3.5 h-3.5" data-id="element-876" data-mp="3bj9pyvu8dfff" />
                      {selectedCall.cliente_telefono}
                    </div>}
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-id="element-877" data-mp="3q4dvb962yboh">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-878" data-mp="3u68tw5wmmayk">
                    Información de Llamada
                  </p>
                  <div className="space-y-2" data-id="element-879" data-mp="3g7izwz53sref">
                    <div className="flex items-center gap-2 text-sm" data-id="element-880" data-mp="3g8tzemrinczq">
                      <UserIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-881" data-mp="3l4j6db5v7w62" />
                      <span className="text-slate-700" data-id="element-882" data-mp="36go8zhgwx3mn">
                        {selectedCall.agente_nombre}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm" data-id="element-883" data-mp="3b9hgue7ogs2y">
                      <CalendarIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-884" data-mp="2xht0fyqebhg6" />
                      <span className="text-slate-700" data-id="element-885" data-mp="32ge4684uwj9d">
                        {format(new Date(selectedCall.fecha), 'd MMM yyyy, h:mm a', {
                      locale: es
                    })}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-2" data-id="element-886" data-mp="24m3bgx725dlb">
                      <Badge variant="secondary" className="capitalize" data-id="element-887" data-mp="24jqo24ij6b0v">
                        {selectedCall.flujo_seleccionado.replace(/_/g, ' ')}
                      </Badge>
                      <Badge variant={selectedCall.resultado === 'venta_realizada' ? 'success' : selectedCall.resultado === 'promesa_compra' || selectedCall.resultado === 'promesa_cobro' ? 'info' : selectedCall.resultado === 'seguimiento' ? 'warning' : 'danger'} data-id="element-888" data-mp="1up708jprydd2">
                        {selectedCall.resultado.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Productos Ofrecidos */}
              {selectedCall.productos_ofrecidos?.length > 0 && <div data-id="element-889" data-mp="3njggs25ppai2">
                  <h4 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2" data-id="element-890" data-mp="3niwz8mjz4o4k">
                    <PackageIcon className="w-4 h-4 text-indigo-500" data-id="element-891" data-mp="3scssjbpalmte" />
                    Productos Ofrecidos (
                    {selectedCall.productos_ofrecidos.length})
                  </h4>
                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-id="element-892" data-mp="54xf9lo6tygj">
                    <table className="w-full text-sm" data-id="element-893" data-mp="a64o7o22h3os">
                      <thead className="bg-slate-50 text-xs text-slate-500" data-id="element-894" data-mp="f4fxnxog9c6h">
                        <tr data-id="element-895" data-mp="f2bvpla42n8u">
                          <th className="px-3 py-2 text-left font-medium" data-id="element-896" data-mp="2bxhpm34tstg3">
                            Producto
                          </th>
                          <th className="px-3 py-2 text-left font-medium" data-id="element-897" data-mp="2gwb706n8h4lj">
                            SKU
                          </th>
                          <th className="px-3 py-2 text-right font-medium" data-id="element-898" data-mp="hpdxziehj4ed">
                            Precio
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100" data-id="element-899" data-mp="rf796iapthi3">
                        {selectedCall.productos_ofrecidos.map((p: any, i: number) => <tr key={i} className="hover:bg-slate-50" data-id="element-900" data-mp="3r62zg10ed5u" data-mpi={i}>
                              <td className="px-3 py-2 font-medium text-slate-900" data-id="element-901" data-mp="3uxh0512wetrw">
                                {p.nombre}
                              </td>
                              <td className="px-3 py-2 text-slate-500" data-id="element-902" data-mp="8o6azwhf5pzl">
                                {p.sku}
                              </td>
                              <td className="px-3 py-2 text-right text-emerald-600 font-medium" data-id="element-903" data-mp="1p4shb3rf5p3h">
                                RD$ {p.precio}
                              </td>
                            </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>}

              {/* Productos de Interés */}
              {selectedCall.productos_interes?.length > 0 && <div data-id="element-904" data-mp="1moaumqvpv0qc">
                  <h4 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2" data-id="element-905" data-mp="1mpua4f9jt7r2">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-500" data-id="element-906" data-mp="2afy2trl77n3a" />
                    Productos de Interés (
                    {selectedCall.productos_interes.length})
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-907" data-mp="21jgzhaj2sti3">
                    {selectedCall.productos_interes.map((p: any, i: number) => <span key={i} className="inline-flex items-center px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium border border-emerald-200" data-id="element-908" data-mp="2tc7s7b1jw02l" data-mpi={i}>
                        {p.nombre} — RD$ {p.precio}
                      </span>)}
                  </div>
                </div>}

              {/* Productos Vencidos del Cliente */}
              {selectedCall.productos_vencidos_cliente?.length > 0 && <div data-id="element-909" data-mp="h5f8i4t6scv4">
                  <h4 className="text-sm font-bold text-rose-700 mb-2 flex items-center gap-2" data-id="element-910" data-mp="3glbx895u09cw">
                    <AlertTriangleIcon className="w-4 h-4 text-rose-500" data-id="element-911" data-mp="3gnolt563hhyx" />
                    Productos Vencidos Reportados (
                    {selectedCall.productos_vencidos_cliente.length})
                  </h4>
                  <div className="bg-rose-50 border border-rose-200 rounded-xl overflow-hidden" data-id="element-912" data-mp="3uktnyu2otwow">
                    <table className="w-full text-sm" data-id="element-913" data-mp="14w1gjw36xx54">
                      <thead className="bg-rose-100 text-xs text-rose-700" data-id="element-914" data-mp="14vjfmm5l2uqv">
                        <tr data-id="element-915" data-mp="19tkvky7pqdy4">
                          <th className="px-3 py-2 text-left font-medium" data-id="element-916" data-mp="19wz0ad3umz8q">
                            Producto
                          </th>
                          <th className="px-3 py-2 text-left font-medium" data-id="element-917" data-mp="2pdag2vtltsdh">
                            SKU
                          </th>
                          <th className="px-3 py-2 text-center font-medium" data-id="element-918" data-mp="2klixp8klqsyf">
                            Cantidad
                          </th>
                          <th className="px-3 py-2 text-left font-medium" data-id="element-919" data-mp="2bm5maacx3ub2">
                            Notas
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-rose-100" data-id="element-920" data-mp="21nzkrtv0wq6q">
                        {selectedCall.productos_vencidos_cliente.map((v: any, i: number) => <tr key={i} data-id="element-921" data-mp="3w2a8fyzvteax" data-mpi={i}>
                              <td className="px-3 py-2 font-medium text-slate-900" data-id="element-922" data-mp="3w17knzxuvrz8">
                                {v.nombre}
                              </td>
                              <td className="px-3 py-2 text-slate-500" data-id="element-923" data-mp="1pe9xygip55vb">
                                {v.sku}
                              </td>
                              <td className="px-3 py-2 text-center font-bold text-rose-700" data-id="element-924" data-mp="1u5i4n4sn6d4s">
                                {v.cantidad}
                              </td>
                              <td className="px-3 py-2 text-slate-600 text-xs" data-id="element-925" data-mp="1ke5gan61y53i">
                                {v.notas || '—'}
                              </td>
                            </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>}

              {/* Notas */}
              {selectedCall.notas && <div data-id="element-926" data-mp="233tvn7xf7cxr">
                  <h4 className="text-sm font-bold text-slate-700 mb-2" data-id="element-927" data-mp="2379caf6qsu1a">
                    Notas
                  </h4>
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-sm text-slate-700 whitespace-pre-wrap" data-id="element-928" data-mp="rpzv5zb36e35">
                    {selectedCall.notas}
                  </div>
                </div>}
            </div>
          </ModalBody>}
      </Modal>
    </div>;
}